import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Clock,
  ArrowRight,
  Pill,
  Building2,
  Globe2,
  Sparkles,
  ClipboardList,
  Home,
  Award,
  Users,
  X,
  Hash,
} from 'lucide-react';
import BlogImg from '../assets/image/Blog.jpg'

const articleAccents = [
  { bar: 'from-teal-400 to-teal-600', icon: 'bg-teal-50 text-teal-600', badge: 'bg-teal-50 text-teal-700 border-teal-200', btn: 'bg-teal-600 hover:bg-teal-700', ring: 'hover:ring-teal-200' },
  { bar: 'from-blue-400 to-blue-600', icon: 'bg-blue-50 text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200', btn: 'bg-blue-600 hover:bg-blue-700', ring: 'hover:ring-blue-200' },
  { bar: 'from-violet-400 to-violet-600', icon: 'bg-violet-50 text-violet-600', badge: 'bg-violet-50 text-violet-700 border-violet-200', btn: 'bg-violet-600 hover:bg-violet-700', ring: 'hover:ring-violet-200' },
  { bar: 'from-amber-400 to-amber-600', icon: 'bg-amber-50 text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200', btn: 'bg-amber-600 hover:bg-amber-700', ring: 'hover:ring-amber-200' },
  { bar: 'from-rose-400 to-rose-600', icon: 'bg-rose-50 text-rose-600', badge: 'bg-rose-50 text-rose-700 border-rose-200', btn: 'bg-rose-600 hover:bg-rose-700', ring: 'hover:ring-rose-200' },
  { bar: 'from-emerald-400 to-emerald-600', icon: 'bg-emerald-50 text-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700', ring: 'hover:ring-emerald-200' },
];

const blogPosts = [
  {
    title: 'Why Quality Medicines Matter for Your Health',
    excerpt: 'Using low-quality or unverified products can lead to serious health risks. High-quality medicines ensure effective recovery, reduced side effects, and consistent, reliable results.',
    category: 'Health & Safety',
    readTime: '5 min read',
    color: 'from-teal-500 to-teal-700',
    Icon: Pill,
    content: 'Substandard or unverified products can carry incorrect dosages, impurities, or weak active ingredients — slowing recovery or causing side effects that a genuine medicine wouldn\'t. At Galaxy Health Care, every product passes strict quality checks and comes only from certified, GMP-compliant manufacturers before it reaches a shelf.',
    highlights: ['Buy only from licensed pharmacies', 'Always check batch number & expiry', 'Ask your pharmacist if unsure of source'],
  },
  {
    title: 'Affordable Healthcare for All: Our Mission',
    excerpt: 'One of the biggest challenges in healthcare is affordability. We believe healthcare should not be a privilege — it should be accessible to everyone, regardless of income or location.',
    category: 'Company Mission',
    readTime: '4 min read',
    color: 'from-blue-500 to-blue-800',
    Icon: Building2,
    content: 'For millions of families, medicine cost decides whether treatment happens at all. By working directly with manufacturers and keeping our supply chain lean, we\'re able to offer trusted medicines at prices that don\'t force anyone to choose between treatment and other essentials.',
    highlights: ['Direct manufacturer partnerships', 'Lower margins, same quality standard', 'Pricing that doesn\'t compromise safety'],
  },
  {
    title: 'Bridging the Urban-Rural Healthcare Gap',
    excerpt: 'Rural areas face limited medical infrastructure and lower health awareness. Galaxy Health Care is actively expanding distribution networks and delivering products to remote areas.',
    category: 'Distribution',
    readTime: '6 min read',
    color: 'from-green-500 to-teal-600',
    Icon: Globe2,
    content: 'Many rural and semi-urban regions still lack easy access to essential medicines. We\'ve extended our distribution network well beyond metro hubs, pairing deliveries with community health awareness drives so smaller towns get both the medicine and the knowledge to use it well.',
    highlights: ['Distribution reaching smaller towns', 'Awareness drives in underserved areas', 'Consistent supply, not one-off drops'],
  },
  {
    title: 'Daily Health & Hygiene Practices',
    excerpt: 'Good health starts with simple daily habits. Regular handwashing, clean water, oral hygiene, adequate sleep, balanced diet, and regular physical activity prevent many common illnesses.',
    category: 'Wellness',
    readTime: '5 min read',
    color: 'from-orange-500 to-amber-600',
    Icon: Sparkles,
    content: 'Most illnesses are prevented, not treated. A handful of consistent daily habits does more for long-term health than most medicine cabinets combined — and none of them cost much at all.',
    highlights: ['Wash hands regularly with soap', 'Sleep 7–8 hours a night', '30 minutes of daily movement'],
  },
  {
    title: 'Safe Use of Medicines: Key Guidelines',
    excerpt: 'Medicines should always be used responsibly. Always follow a doctor\'s prescription, avoid self-medication, check expiry dates, and follow recommended dosage instructions.',
    category: 'Health & Safety',
    readTime: '4 min read',
    color: 'from-purple-500 to-indigo-600',
    Icon: ClipboardList,
    content: 'Medicine works because it changes how your body functions — which is exactly why it needs to be taken with care. Stopping a course early or guessing a dosage are two of the most common, and most avoidable, mistakes.',
    highlights: ['Finish the full prescribed course', 'Avoid self-medicating beyond mild symptoms', 'Store out of children\'s reach'],
  },
  {
    title: 'Proper Storage of Medicines at Home',
    excerpt: 'Improper storage can reduce the effectiveness of medicines. Store in a cool, dry place, keep away from direct sunlight, and always close containers tightly after use.',
    category: 'Healthcare Tips',
    readTime: '3 min read',
    color: 'from-pink-500 to-rose-600',
    Icon: Home,
    content: 'A well-made medicine can still lose its effectiveness if it\'s stored badly at home. Heat, light, and moisture are the real enemies — and the bathroom cabinet, despite being the most common spot, is often the worst choice.',
    highlights: ['Cool, dry place away from sunlight', 'Refrigerate only if the label says so', 'Recheck expiry dates every few months'],
  },
  {
    title: 'Choosing the Right Personal Care Products',
    excerpt: 'Selecting the right personal care products is essential for maintaining skin and body health. Consider suitability for your skin type, safe ingredients, and trusted brands.',
    category: 'Cosmetics',
    readTime: '4 min read',
    color: 'from-teal-400 to-cyan-600',
    Icon: Sparkles,
    content: 'Personal care products touch your skin every day, so the choice matters as much as any other health decision. Knowing your skin type and reading the ingredient list beats following a trend every time.',
    highlights: ['Match products to your skin type', 'Avoid harsh sulfates & fragrances', 'Choose certified, trusted brands'],
  },
  {
    title: 'Our Commitment to Society',
    excerpt: 'At Galaxy Health Care, we believe in more than just business — we believe in responsibility. Promoting health awareness, supporting underserved communities, and delivering affordable healthcare.',
    category: 'Company',
    readTime: '5 min read',
    color: 'from-indigo-600 to-blue-800',
    Icon: Users,
    content: 'Our responsibility doesn\'t end at the sale of a product. We run health awareness programs and partner with local pharmacies and health workers to bring both medicine and knowledge to communities that need them most.',
    highlights: ['Community health awareness programs', 'Support for underserved regions', 'Partnerships with local health workers'],
  },
];

const highlightPost = {
  title: 'Health & Wellness: Our Vision for a Healthier India',
  excerpt: 'In today\'s fast-moving world, maintaining good health has become more important than ever. Access to safe, effective, and affordable healthcare is not just a necessity — it is a fundamental right.',
  category: 'Featured',
  readTime: '8 min read',
  Icon: Award,
  content: 'We see healthcare as a fundamental right, not a privilege. That belief shapes how we source, price, and distribute every product — measured against one question: does this bring safe, affordable healthcare closer to more people, wherever they live?',
  highlights: ['Quality: rigorous standards on every product', 'Affordability: pricing that never excludes', 'Reach: closing the urban-rural gap'],
};

function ArticlePopup({ post, onClose }) {
  const accent = post?.accent || articleAccents[0];

  useEffect(() => {
    const original = document.body.style.overflow;

    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!post) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-[fadeIn_0.15s_ease-out]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white w-full max-w-[520px] rounded-2xl shadow-2xl overflow-hidden animate-[popIn_0.18s_ease-out] border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`h-1.5 w-full bg-gradient-to-r ${accent.bar}`} />
        <div className="px-6 pt-6 pb-5 relative">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
          >
            <X size={16} />
          </button>
          <div className={`mb-4 grid h-12 w-12 place-items-center rounded-xl ${accent.icon}`}>
            <post.Icon size={24} strokeWidth={1.8} />
          </div>
          <div className={`mb-3 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${accent.badge}`}>
            <Hash size={9} /> {post.category}
          </div>
          <h3 className="pr-10 text-2xl font-black leading-tight text-slate-900">{post.title}</h3>
          <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-400">
            <Clock size={12} /> {post.readTime}
          </div>
        </div>

        <div className="mx-6 h-px bg-gradient-to-r from-slate-100 via-slate-200 to-transparent" />

        <div className="px-6 py-5 max-h-[50vh] overflow-y-auto">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">{post.content}</p>
          {post.highlights && (
            <ul className="space-y-2 mb-1">
              {post.highlights.map((point) => (
                <li key={point} className="flex items-start gap-2 text-gray-600 text-xs sm:text-sm">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-gradient-to-r ${accent.bar}`} />
                  {point}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className={`w-full rounded-full px-5 py-3 text-sm font-bold text-white shadow transition-all hover:shadow-lg ${accent.btn}`}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Blog() {
  const [activePost, setActivePost] = useState(null);

  return (
    <div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes popIn { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      `}</style>

      {/* Hero */}
      <img src={BlogImg} alt="Blog" className="w-full h-auto block" />

      {/* Featured post */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="gradient-bg rounded-3xl p-6 sm:p-8 md:p-12 text-white relative overflow-hidden reveal">
            <highlightPost.Icon
              size={160}
              strokeWidth={1.25}
              className="absolute -top-6 -right-6 opacity-10 pointer-events-none"
            />
            <div className="relative z-10 max-w-2xl">
              <div className="inline-block bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                {highlightPost.category}
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 leading-tight">{highlightPost.title}</h2>
              <p className="text-white/80 leading-relaxed mb-6 text-sm sm:text-base">{highlightPost.excerpt}</p>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2 text-white/60 text-sm">
                  <Clock size={14} /> {highlightPost.readTime}
                </div>
                <button
                  type="button"
                  onClick={() => setActivePost(highlightPost)}
                  className="bg-white text-teal-600 font-bold px-6 py-2.5 rounded-xl inline-flex items-center gap-2 hover:shadow-lg transition-all text-sm"
                >
                  Read Article <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog grid */}
      <section className="py-12 pb-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 reveal">
            <div className="gradient-text text-sm font-bold uppercase tracking-widest mb-3">Latest Articles</div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900">Health & Wellness Topics</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogPosts.map((post, i) => (
              (() => {
                const accent = articleAccents[i % articleAccents.length];
                const article = { ...post, accent };
                return (
                  <article
                    key={post.title}
                    className={`reveal delay-${(i % 4 + 1) * 100} group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:ring-2 ${accent.ring}`}
                    onClick={() => setActivePost(article)}
                  >
                    <div className={`h-1.5 w-full bg-gradient-to-r ${accent.bar}`} />
                    <span className="pointer-events-none absolute right-4 top-3 select-none text-6xl font-black text-slate-100 transition-all duration-300 group-hover:scale-110 group-hover:text-slate-200">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex flex-1 flex-col p-6">
                      <div className={`inline-grid h-12 w-12 shrink-0 place-items-center rounded-xl ${accent.icon} transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110`}>
                        <post.Icon size={24} strokeWidth={1.8} />
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${accent.badge}`}>
                          <Hash size={9} />{post.category}
                        </span>
                      </div>
                      <h3 className="mt-3 text-xl font-black leading-tight text-slate-900 transition-colors group-hover:text-slate-950">{post.title}</h3>
                      <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-500">{post.excerpt}</p>
                      <div className="my-4 h-px bg-gradient-to-r from-slate-100 via-slate-200 to-transparent" />
                      <div className="mt-auto flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                          <Clock size={12} /> {post.readTime}
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActivePost(article);
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white shadow transition-all group-hover:gap-2.5 ${accent.btn}`}
                        >
                          Read More <ArrowRight size={13} />
                        </button>
                      </div>
                    </div>
                    <div className={`absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r ${accent.bar} transition-all duration-500 group-hover:w-full`} />
                  </article>
                );
              })()
            ))}
          </div>
        </div>
      </section>

      {/* Health tips section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 reveal">
            <div className="gradient-text text-sm font-bold uppercase tracking-widest mb-3">Quick Health Tips</div>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900">Essential Daily Practices</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              {
                title: 'Safe Medicine Usage',
                Icon: Pill,
                tips: ['Always follow a doctor\'s prescription', 'Avoid self-medication for strong drugs', 'Check expiry dates before use', 'Follow recommended dosage', 'Keep medicines out of children\'s reach']
              },
              {
                title: 'Proper Medicine Storage',
                Icon: Home,
                tips: ['Store in a cool, dry place', 'Keep away from direct sunlight', 'Close containers tightly after use', 'Use refrigeration only if recommended', 'Monitor expiry dates regularly']
              },
              {
                title: 'Daily Hygiene',
                Icon: Sparkles,
                tips: ['Regular handwashing with quality handwash', 'Drink clean and safe water', 'Maintain oral hygiene', 'Get adequate sleep (7-8 hours)', 'Follow a balanced diet']
              },
              {
                title: 'Personal Care',
                Icon: ClipboardList,
                tips: ['Choose products suitable for your skin type', 'Look for safe and gentle ingredients', 'Avoid harsh chemicals', 'Use trusted and reliable brands', 'Check for certifications and standards']
              },
            ].map((section, i) => (
              <div key={section.title} className={`reveal delay-${(i % 2 + 1) * 200} bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-6`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl gradient-bg flex items-center justify-center text-white flex-shrink-0">
                    <section.Icon size={22} strokeWidth={2} />
                  </div>
                  <h3 className="font-bold text-xl text-gray-900">{section.title}</h3>
                </div>
                <ul className="space-y-2">
                  {section.tips.map(tip => (
                    <li key={tip} className="flex items-start gap-2 text-gray-600 text-sm">
                      <div className="w-1.5 h-1.5 gradient-bg rounded-full mt-2 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ArticlePopup post={activePost} onClose={() => setActivePost(null)} />
    </div>
  );
}
